import { ReactElement, useMemo, useState } from 'react';
import { DatePicker as MuiXDatePicker, DateValidationError, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment-timezone';
import { useFormikContext } from 'formik';
import get from 'lodash/get';
import classNames from 'classnames';

// Core + Store
import { DEFAULT_DATE_FORMAT, DEFAULT_TIMEZONE } from 'core/constants';
import { IFormikDatePickerProps, TimesOfDay } from '../types';

export const FormikDatePicker = (props: IFormikDatePickerProps): ReactElement => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const {
    className,
    disabled,
    disablePast = false,
    field,
    format = props.format || DEFAULT_DATE_FORMAT,
    formikKey,
    fullWidth,
    label,
    maxDate,
    minDate,
    onBlur,
    onChange,
    required,
    style,
    timeOfDay,
    views,
    openTo,
    placeholder,
    ...rest
  } = props;
  const { values, setFieldValue } = useFormikContext();
  const { name } = field;
  const monthDayFormat = format === 'MM/DD';

  // restrict the min and max date to 1904 when the format is 'MM/DD' unless a min/maxDate is passed in
  const minimumDate = monthDayFormat ? moment.tz(`1904-01-01`, DEFAULT_TIMEZONE) : minDate ? moment(minDate) : null;
  const maximumDate = monthDayFormat ? moment.tz(`1904-12-31`, DEFAULT_TIMEZONE) : maxDate ? moment(maxDate) : null;

  // Disables the currently selected date.
  const shouldDisableSelectedDate = (day: Moment): boolean => {
    const selectedDates: Date[] | Date = get(values, formikKey, []);

    // Dates can be a single date or multiple dates. Always return false when we only have 1 date.
    if (!Array.isArray(selectedDates)) return false;

    // Handle multiple dates.
    return !!(selectedDates as Date[])?.find((date: Date): boolean =>
      moment(moment(date).format(DEFAULT_DATE_FORMAT)).isSame(day.format(DEFAULT_DATE_FORMAT)),
    );
  };

  const handleChange = (date: Moment): void => {
    let dateValue: Moment;
    const finalDate: Moment = date;

    if (views?.includes('year')) {
      dateValue = moment().year(finalDate?.year());
    } else {
      if (finalDate) {
        // We send/save ISO string to the server, adjusting the time of day as needed
        const dateToSave = moment(finalDate).tz(DEFAULT_TIMEZONE).startOf('day');
        switch (timeOfDay) {
          case TimesOfDay.Noon:
            dateToSave.set('hour', 12);
            break;
          case TimesOfDay.EndOfDay:
            dateToSave.set('hour', 23).set('minute', 59);
            break;
          case TimesOfDay.Midnight:
          default:
            dateToSave.startOf('day');
            break;
        }
        dateValue = moment(dateToSave?.toISOString() || dateToSave?.toString());
      } else {
        dateValue = finalDate;
      }
    }
    setFieldValue(field?.name, dateValue, true);
    onChange?.(field?.name, dateValue);
  };

  /**
   * If an error occurs when entering a date we want to make sure we check if it is a leap year date (02/29).
   * Moment validates the date before giving us time to see the actual value, just showing us 'invalid date' instead.
   * Getting the element value works around that and allows us to set the year to a leap year.
   * This is to make sure a user can enter 02/29 for the date of birth inputs, like in learner validation.
   */
  const checkForLeapYearDate = () => {
    if (monthDayFormat) {
      const rawValue: string = document.getElementsByName('dob')[0]?.getAttribute('value');
      if ((rawValue !== null || undefined) && rawValue === '02/29') {
        const rawDate: Moment = moment.tz('1904-02-29', DEFAULT_TIMEZONE);
        handleChange(rawDate);
      }
    }
  };

  /**
   * Moment cannot handle the date repeater validation text color so it is done manually here.
   * slotProps => textField => color does not work
   */
  const handleDateRepeaterValidation = () => {
    const selectedDates: Moment[] | Moment = get(values, formikKey, []);

    if (Array.isArray(selectedDates)) {
      selectedDates?.forEach((date: Moment) => {
        if (date.toString() !== '') {
          const element = document.getElementsByName(field.name);
          if (moment(date)?.isValid()) {
            element[0].style.color = 'black';
            setError(null);
          } else {
            element[0].style.color = '#bd2e2a';
            setError('invalidDate');
          }
        }
      });
    } else {
      if (field.value.toString() !== '') {
        if (moment(field.value)?.isValid()) {
          setError(null);
        } else {
          setError('invalidDate');
        }
      }
    }
  };

  const [error, setError] = useState<DateValidationError | null>(null);

  const errorMessage = useMemo(() => {
    switch (error) {
      case 'invalidDate': {
        return 'Invalid date format';
      }
      case null: {
        return '';
      }

      default: {
        return '';
      }
    }
  }, [error]);

  // Convert the date from ISO UTC to `DEFAULT_TIMEZONE` on display.
  const displayValue = (date: Moment | undefined): moment.Moment => {
    let dateValue: moment.Moment;

    if (views?.includes('year')) {
      dateValue = date;
    } else {
      dateValue = moment(date).tz(DEFAULT_TIMEZONE);
    }

    return dateValue;
  };

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <MuiXDatePicker
          sx={{ ...(format === 'MM/DD' && { width: '60%' }) }}
          className={classNames('date-picker', className)}
          disabled={disabled}
          disablePast={disablePast}
          slotProps={{
            toolbar: { hidden: true },
            textField: {
              required: required,
              fullWidth: fullWidth,
              variant: 'outlined',
              onBlur: onBlur,
              style: style,
              helperText: errorMessage,
              placeholder: placeholder,
            },
            openPickerButton: {
              style: { marginLeft: '0' },
            },
            inputAdornment: { position: 'start' },
          }}
          format={format || DEFAULT_DATE_FORMAT}
          label={label}
          maxDate={maximumDate}
          minDate={minimumDate}
          name={name}
          onChange={handleChange}
          onClose={(): void => setIsOpen(false)}
          onOpen={(): void => setIsOpen(true)}
          onError={() => {
            if (monthDayFormat) checkForLeapYearDate();
            else handleDateRepeaterValidation();
          }}
          open={isOpen}
          readOnly={disabled}
          shouldDisableDate={shouldDisableSelectedDate}
          value={field?.value ? moment(displayValue(field?.value)) : null}
          views={views}
          openTo={openTo}
          {...rest}
        />
      </LocalizationProvider>
    </>
  );
};
